import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Save, Camera, Zap, AlertCircle, Upload, FileImage, CheckCircle, FolderOpen, Layers, ArrowRight, Trash2, Download } from 'lucide-react';

/**
 * PaddleOCR Training Data Management Panel
 */
function TrainingDataPanel({ apiBaseUrl, onSelectImage }) {
    const [activeTab, setActiveTab] = useState('raw'); // 'raw' | 'dataset'
    const [dataset, setDataset] = useState([]);
    const [rawImages, setRawImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [trainingStatus, setTrainingStatus] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const fileInputRef = useRef(null);

    // Initial Load
    useEffect(() => {
        if (activeTab === 'dataset') {
            loadDataset();
        } else {
            loadRawImages();
        }
    }, [activeTab]);

    const loadDataset = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/learning/dataset`);
            const data = await response.json();
            if (data.success) {
                setDataset(data.data);
            }
        } catch (error) {
            console.error('Failed to load dataset:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRawImages = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/learning/raw-images`);
            const data = await response.json();
            if (data.success) {
                setRawImages(data.data);
            }
        } catch (error) {
            console.error('Failed to load raw images:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteImage = async (filename, e) => {
        e.stopPropagation(); // Prevent image selection
        if (!window.confirm(`Delete ${filename}?`)) return;

        try {
            setLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/learning/raw-images/${encodeURIComponent(filename)}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                loadRawImages(); // Refresh list
            } else {
                alert('Failed to delete image: ' + data.message);
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('삭제 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const formData = new FormData();
        Array.from(files).forEach(file => {
            formData.append('images', file);
        });

        try {
            setLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/learning/upload-raw`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                alert(`${data.message}`);
                loadRawImages();
            } else {
                alert('Upload failed: ' + data.error);
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('업로드 실패');
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const generateDataset = async () => {
        if (!window.confirm('기존 데이터를 바탕으로 학습용 데이터셋(Crop Image)을 생성하시겠습니까?\n(기존 데이터는 덮어씌워집니다)')) return;
        try {
            setLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/learning/generate-dataset`, { method: 'POST' });
            const data = await response.json();
            if (data.success) {
                alert(data.message);
                loadDataset(); // Reload after generation
            } else {
                alert('생성 실패: ' + data.error);
            }
        } catch (error) {
            console.error('Failed to generate dataset:', error);
            alert('데이터셋 생성 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const startTraining = async () => {
        if (!window.confirm('PaddleOCR 학습을 시작하시겠습니까?\n(백그라운드에서 실행됩니다)')) return;
        try {
            const response = await fetch(`${apiBaseUrl}/api/learning/train`, { method: 'POST' });
            const data = await response.json();
            if (data.success) {
                alert(data.message);
                setTrainingStatus('🟢 학습이 시작되었습니다... (Training Started)');
            }
        } catch (error) {
            alert('학습 시작 실패');
            console.error(error);
        }
    };

    // ⭐ Export Training Data as PPOCRLabel format (ZIP download)
    const exportDataset = async () => {
        try {
            setLoading(true);
            // Use existing export-ppocrlabel endpoint that returns ZIP
            const response = await fetch(`${apiBaseUrl}/api/learning/export-ppocrlabel`);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Export failed');
            }

            // Download as blob
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `paddleocr_training_${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            alert('학습 데이터 내보내기 완료!\nPPOCRLabel 포맷으로 저장되었습니다.');
        } catch (error) {
            console.error('Export error:', error);
            alert('내보내기 실패: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 h-full flex flex-col relative w-full">
            {/* Header / Actions */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex-shrink-0">
                <div className="flex border-b border-gray-100">
                    <button
                        onClick={() => setActiveTab('raw')}
                        className={`flex-1 py-2 text-xs font-semibold flex items-center justify-center gap-2 ${activeTab === 'raw' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        <FolderOpen className="w-3.5 h-3.5" /> Source Images
                    </button>
                    <button
                        onClick={() => setActiveTab('dataset')}
                        className={`flex-1 py-2 text-xs font-semibold flex items-center justify-center gap-2 ${activeTab === 'dataset' ? 'bg-indigo-50 text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        <Layers className="w-3.5 h-3.5" /> Training Dataset
                    </button>
                </div>

                <div className="p-4 bg-gray-50/50">
                    {activeTab === 'raw' ? (
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-sm font-bold text-gray-800">Raw Images Management</h3>
                                <p className="text-xs text-gray-500 mt-1">Upload images to label for training.</p>
                            </div>
                            <div>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*,application/pdf"
                                    className="hidden"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={loading}
                                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                                >
                                    <Upload className="w-3.5 h-3.5" />
                                    Upload Images
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-sm font-bold text-gray-800">Dataset Generation</h3>
                                <p className="text-xs text-gray-500 mt-1">Generate cropped images from labeled data.</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={generateDataset}
                                    disabled={loading}
                                    className={`px-3 py-2 rounded font-bold text-xs flex items-center gap-1.5 transition-colors shadow-sm
                                        ${loading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-blue-200 text-blue-700 hover:bg-blue-50'}
                                    `}
                                >
                                    {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                    Generate Crops
                                </button>
                                <button
                                    onClick={startTraining}
                                    disabled={loading}
                                    className="px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                                >
                                    <Zap className="w-3.5 h-3.5 fill-current" />
                                    Start Training
                                </button>
                                <button
                                    onClick={exportDataset}
                                    disabled={loading || dataset.length === 0}
                                    className={`px-3 py-2 rounded font-bold text-xs flex items-center gap-1.5 transition-colors shadow-sm
                                        ${loading || dataset.length === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}
                                    `}
                                    title="Download training data as ZIP (PPOCRLabel format)"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    Export ZIP
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {trainingStatus && (
                <div className="p-2.5 bg-green-50 border border-green-200 rounded text-xs text-green-800 font-medium flex items-center gap-2 animate-fadeIn flex-shrink-0">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    {trainingStatus}
                </div>
            )}

            {/* Grid Content */}
            <div className="flex-1 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                <div className="flex-1 overflow-y-auto p-3 bg-gray-50/50">
                    {loading && (dataset.length === 0 && rawImages.length === 0) ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <RefreshCw className="w-8 h-8 animate-spin mb-2 text-blue-400" />
                            <p className="text-sm">{activeTab === 'raw' ? 'Loading images...' : 'Loading dataset...'}</p>
                        </div>
                    ) : activeTab === 'raw' ? (
                        // RAW IMAGES GRID
                        rawImages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center p-8 text-gray-400">
                                <FileImage className="w-12 h-12 mb-3 opacity-20" />
                                <p className="text-sm font-medium text-gray-500">No images uploaded yet.</p>
                                <p className="text-xs text-gray-400 mt-1">Upload images to start labeling.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-3">
                                {rawImages.map((img, idx) => (
                                    <div key={idx} className="group relative aspect-square bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden cursor-pointer"
                                        onClick={() => onSelectImage && onSelectImage(`${apiBaseUrl}${img.url}`)}
                                    >
                                        <img src={`${apiBaseUrl}${img.url}`} alt={img.name} className="w-full h-full object-cover" loading="lazy" />

                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-end p-2 pointer-events-none">
                                            <div className="w-full bg-white/90 backdrop-blur rounded p-1.5 text-xs truncate font-mono shadow-sm flex justify-between items-center group-hover:bg-white transition-colors pointer-events-auto">
                                                <div className="flex items-center min-w-0">
                                                    <span className="truncate">{img.name}</span>
                                                    {img.isLabeled && <CheckCircle className="w-3.5 h-3.5 text-green-500 ml-1 flex-shrink-0" />}
                                                </div>
                                                <button
                                                    onClick={(e) => handleDeleteImage(img.name, e)}
                                                    className="p-1 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded transition-colors ml-1 flex-shrink-0"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Hover Action */}
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-0 transition-opacity pointer-events-none">
                                            {/* Disabled Label Button in favor of click-to-label behavior */}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        // DATASET GRID (Existing Code Logic)
                        dataset.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center p-8">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <AlertCircle className="w-8 h-8 text-gray-400" />
                                </div>
                                <h4 className="text-gray-800 font-medium mb-1">학습 데이터가 없습니다</h4>
                                <p className="text-gray-500 text-xs max-w-xs mb-4">
                                    아직 생성된 Crop 이미지가 없습니다.<br />
                                    먼저 이미지를 라벨링하고 저장한 뒤, <b>[Generate Crops]</b> 버튼을 눌러주세요.
                                </p>
                            </div>
                        ) : (
                            <table className="w-full text-xs text-left border-collapse">
                                <thead className="bg-gray-100 text-gray-600 font-medium sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="px-4 py-2 border-b w-[120px] text-center">Image</th>
                                        <th className="px-4 py-2 border-b">Label (Transcription)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {dataset.map((item, idx) => (
                                        <tr
                                            key={idx}
                                            className="hover:bg-blue-50 transition-colors cursor-pointer"
                                            onDoubleClick={() => setPreviewImage(item)}
                                        >
                                            <td className="px-4 py-2 border-r text-center">
                                                <div className="h-8 flex justify-center items-center">
                                                    <img
                                                        src={`${apiBaseUrl}/${item.image}`}
                                                        alt={item.label}
                                                        className="max-h-full max-w-full object-contain rounded border border-gray-200 hover:scale-150 transition-transform origin-center z-0 hover:z-10 bg-white shadow-sm"
                                                        loading="lazy"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPreviewImage(item);
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 font-mono text-sm text-gray-700 truncate max-w-[200px]" title={item.label}>
                                                {item.label}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}
                </div>
            </div >

            {/* Image Preview Modal */}
            {
                previewImage && (
                    <div
                        className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8 animate-fadeIn"
                        onClick={() => setPreviewImage(null)}
                    >
                        <div
                            className="bg-white p-2 rounded-lg shadow-2xl max-w-full max-h-full flex flex-col gap-2"
                            onClick={e => e.stopPropagation()}
                        >
                            <img
                                src={`${apiBaseUrl}/${previewImage.image}`}
                                alt={previewImage.label}
                                className="max-w-full max-h-[80vh] object-contain rounded border border-gray-200"
                            />
                            <div className="text-center">
                                <span className="inline-block px-3 py-1 bg-gray-100 rounded text-sm font-bold text-gray-800 border border-gray-200">
                                    {previewImage.label}
                                </span>
                            </div>
                            <button
                                onClick={() => setPreviewImage(null)}
                                className="absolute top-4 right-4 text-white hover:text-gray-200 bg-black/20 hover:bg-black/40 rounded-full p-1 transition"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

export default TrainingDataPanel;
